(function () {
  'use strict';

  class SaHumanoidDesign extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = -1;
      this.frame = null;
      this.mediaFrame = null;
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;

      this.track = this.querySelector('[data-hd-track]');
      this.sticky = this.querySelector('[data-hd-sticky]');
      this.header = this.querySelector('.sa-humanoid-design__header');
      this.stage = this.querySelector('.sa-humanoid-design__stage');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-hd-step]'));
      this.mediaItems = Array.prototype.slice.call(this.querySelectorAll('[data-hd-media-item]'));
      if (!this.track || !this.sticky || !this.stage || this.steps.length === 0) return;

      if (this.header && 'ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(this.updateHeaderHeight);
        this.headerResizeObserver.observe(this.header);
      }
      this.updateHeaderHeight();

      this.desktopQuery = window.matchMedia('(min-width: 768px)');
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', this.handleViewportChange);
      } else {
        this.desktopQuery.addListener(this.handleViewportChange);
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      this.setActive(0);
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.connected = false;
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);

      if (this.desktopQuery) {
        if (this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.handleViewportChange);
        } else {
          this.desktopQuery.removeListener(this.handleViewportChange);
        }
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
        this.headerResizeObserver = null;
      }

      if (this.frame !== null) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      if (this.mediaFrame !== null) {
        window.cancelAnimationFrame(this.mediaFrame);
        this.mediaFrame = null;
      }
    }

    handleViewportChange(event) {
      if (event && event.type === 'scroll') {
        this.removeAttribute('data-hd-initial');
      }

      if (this.frame !== null) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateFromScroll();
      });
    }

    updateHeaderHeight() {
      const headerHeight = this.header ? this.header.getBoundingClientRect().height : 0;
      this.style.setProperty('--sa-hd-header-height', `${Math.ceil(headerHeight)}px`);
    }

    updateFromScroll() {
      if (this.steps.length < 2) {
        this.setActive(0);
        return;
      }

      const rect = this.track.getBoundingClientRect();
      const stickyHeight = this.sticky.getBoundingClientRect().height;
      const stickyTop = Number.parseFloat(window.getComputedStyle(this.sticky).top) || 0;
      const scrollDistance = Math.max(1, rect.height - stickyHeight);
      const progress = Math.max(0, Math.min(1, (stickyTop - rect.top) / scrollDistance));
      const nextIndex = Math.round(progress * (this.steps.length - 1));
      this.setActive(nextIndex);
    }

    setActive(index) {
      const nextIndex = Math.max(0, Math.min(index, this.steps.length - 1));
      if (nextIndex === this.activeIndex) return;
      const previousIndex = this.activeIndex;
      const previousMedia = this.mediaItems[previousIndex];
      const nextMedia = this.mediaItems[nextIndex];
      const sameMedia =
        previousMedia && nextMedia && this.mediaKey(previousMedia) === this.mediaKey(nextMedia);
      if (sameMedia) {
        previousMedia.classList.add('is-instant');
        nextMedia.classList.add('is-instant');
      }
      this.activeIndex = nextIndex;

      this.steps.forEach((step, itemIndex) => {
        const isActive = itemIndex === nextIndex;
        const isBefore = itemIndex < nextIndex;
        step.classList.toggle('is-active', isActive);
        step.classList.toggle('is-before', isBefore);
        step.classList.toggle('is-after', itemIndex > nextIndex);
        step.style.setProperty(
          '--sa-hd-stack-order',
          String(isBefore ? itemIndex : this.steps.length - 1 - itemIndex)
        );
        if (isActive) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });

      this.mediaItems.forEach((media, itemIndex) => {
        const isActive = itemIndex === nextIndex;
        media.classList.toggle('is-active', isActive);
        media.classList.toggle('is-before', itemIndex < nextIndex);
        media.classList.toggle('is-after', itemIndex > nextIndex);
        media.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (sameMedia) {
        if (this.mediaFrame !== null) window.cancelAnimationFrame(this.mediaFrame);
        this.mediaFrame = window.requestAnimationFrame(() => {
          previousMedia.classList.remove('is-instant');
          nextMedia.classList.remove('is-instant');
          this.mediaFrame = null;
        });
      }
    }

    mediaKey(media) {
      const image = media.querySelector('img');
      return image ? image.currentSrc || image.getAttribute('src') || '' : '';
    }

    handleBlockSelect(event) {
      const step = event.target && event.target.closest && event.target.closest('[data-hd-step]');
      if (!step || !this.contains(step)) return;
      const index = this.steps.indexOf(step);
      if (index >= 0) this.setActive(index);
    }
  }

  if (!customElements.get('sa-humanoid-design')) {
    customElements.define('sa-humanoid-design', SaHumanoidDesign);
  }
})();
